import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { Mod, BeatmapGoal, Bounty } from 'src/app/interfaces/Bounty';
import { atLeastOne } from 'src/app/_helpers/at-least-one';

@Component({
  selector: 'app-beatmap-goals-fields',
  templateUrl: './beatmap-goals-fields.component.html',
  styleUrls: ['./beatmap-goals-fields.component.scss'],
})
export class BeatmapGoalsFieldsComponent implements OnInit, OnDestroy {
  @Input() parentForm: FormGroup;
  @Input() submitFailed: boolean;
  @Input() bounty?: Bounty;

  public beatmapGoals: { [goal in BeatmapGoal]: boolean } = {
    rank1: false,
    fc: false,
    misses: false,
    accuracy: false,
    mods: false,
    no_difficulty_reduction: true,
  };
  public mods: { [mod in Mod]: boolean } = {
    nomod: false,
    hd: false,
    hr: false,
    dt: false,
    ht: false,
    ez: false,
    fl: false,
    rx: false,
    pf: false,
    sd: false,
    nf: false,
  };
  public modsArray = Object.keys(this.mods);
  public numberOfMisses: number;
  public accuracy: number;
  private subscriptions = [];
  private conditionalValidationRules = {
    numberOfMisses: {
      condition: 'misses',
      validators: [Validators.min(1), Validators.max(1000), Validators.required],
    },
    accuracy: {
      condition: 'accuracy',
      validators: [Validators.min(1), Validators.max(100), Validators.required],
    },
    mods: {
      condition: 'mods',
      validators: [atLeastOne()],
    },
  };

  constructor() {}

  ngOnInit() {
    if (this.bounty) {
      this.mapBountyToState();
    }
    const controls = {
      beatmapGoals: new FormGroup(
        {
          rank1: new FormControl(this.beatmapGoals.rank1),
          fc: new FormControl(this.beatmapGoals.fc),
          misses: new FormControl(this.beatmapGoals.misses),
          accuracy: new FormControl(this.beatmapGoals.accuracy),
          mods: new FormControl(this.beatmapGoals.mods),
          no_difficulty_reduction: new FormControl(
            this.beatmapGoals.no_difficulty_reduction,
          ),
        },
        [atLeastOne()],
      ),
      mods: new FormGroup({
        nomod: new FormControl(this.mods.nomod),
        hd: new FormControl(this.mods.hd),
        hr: new FormControl(this.mods.hr),
        dt: new FormControl(this.mods.dt),
        ht: new FormControl(this.mods.ht),
        ez: new FormControl(this.mods.ez),
        fl: new FormControl(this.mods.fl),
        rx: new FormControl(this.mods.rx),
        pf: new FormControl(this.mods.pf),
        sd: new FormControl(this.mods.sd),
        nf: new FormControl(this.mods.nf),
      }),
      numberOfMisses: new FormControl(this.numberOfMisses),
      accuracy: new FormControl(this.accuracy, [
        Validators.min(1),
        Validators.max(100),
      ]),
    };

    Object.keys(controls).forEach(name => {
      if (!this.parentForm.controls[name]) {
        this.parentForm.addControl(name, controls[name]);
      }
    });

    this.applyConditionalValidators();

    // When beatmap goals change, update validators
    const beatmapGoalsSub = this.parentForm.controls.beatmapGoals.valueChanges.subscribe(
      () => {
        this.applyConditionalValidators();
      },
    );

    this.subscriptions = [beatmapGoalsSub];
  }

  private arrayToBoolObj(array: any[]) {
    return array.reduce(
      (acc, curr) => {
        acc[curr] = true;
        return acc;
      },
      <any>{},
    );
  }
  private mapBountyToState() {
    const bounty = this.bounty;

    this.beatmapGoals = this.arrayToBoolObj(bounty.beatmap_goals || []);
    this.numberOfMisses = bounty.misses;
    this.accuracy = bounty.accuracy;
    this.mods = this.arrayToBoolObj(bounty.mods || []);
  }

  ngOnDestroy() {
    this.applyConditionalValidators(true);
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  get hasMisses() {
    return this.parentForm.get('beatmapGoals').value.misses === true;
  }

  get hasAccuracy() {
    return this.parentForm.get('beatmapGoals').value.accuracy === true;
  }

  get hasMods() {
    return this.parentForm.get('beatmapGoals').value.mods === true;
  }

  public hasError(controlKey: string) {
    const control = this.parentForm.get(controlKey);
    const touchedOrSubmitted = !control.pristine || this.submitFailed;

    return control.errors && touchedOrSubmitted;
  }

  private applyConditionalValidators(forceClear?: boolean) {
    Object.keys(this.conditionalValidationRules).forEach(name => {
      const { condition, validators } = this.conditionalValidationRules[name];
      const isActive = !!!!this.parentForm.controls.beatmapGoals.get(condition)
        .value;

      if (isActive && !forceClear) {
        this.parentForm.controls[name].setValidators(validators);
      } else {
        this.parentForm.controls[name].clearValidators();
      }
      this.parentForm.controls[name].updateValueAndValidity();
    });
  }
}
